import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import { useAuth } from './contexts';
import { AssistantProvider } from './contexts/AssistantContext';
import AssistantPopup from './components/assistant/AssistantPopup';
import { DoordashConnectProvider } from './components/doordash/DoordashConnect';
import OpenAssistantRedirect from './components/assistant/OpenAssistantRedirect';
import NotFound from "pages/NotFound";
import SignInPrompt from "components/SignInPrompt";

const OrderHistoryManagement = lazy(() => import('./pages/order-history-management'));
const LoginRegistration = lazy(() => import('./pages/login-registration'));
const DashboardHome = lazy(() => import('./pages/dashboard-home'));
const CalendarOrderScheduling = lazy(() => import('./pages/calendar-order-scheduling'));
const TeamSetup = lazy(() => import('./pages/team-setup'));
const TeamMembersManagement = lazy(() => import('./pages/team-members-management'));
const PaymentMethodsBilling = lazy(() => import('./pages/payment-methods-billing'));
const TermsConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const SmsOptInProof = lazy(() => import('./pages/SmsOptInProof'));
const AwaitingEmailVerificationPage = lazy(() => import('pages/awaiting-email-verification'));
const ResetPassword = lazy(() => import('pages/reset-password'));
const HomeRestaurantDiscovery = lazy(() => import('./pages/home-restaurant-discovery'));
const RestaurantDetailMenu = lazy(() => import('./pages/restaurant-detail-menu'));
const ShoppingCartCheckout = lazy(() => import('./pages/shopping-cart-checkout'));
const SharedCartMenu = lazy(() => import('./pages/shared-cart'));
const SharedTemplatePage = lazy(() => import('./pages/shared-template'));
const PayPage = lazy(() => import('./pages/payments'));
const OrderSuccess = lazy(() => import('./pages/order-success'));
const ProfilePage = lazy(() => import('./pages/profile'));
const SavesPage = lazy(() => import('./pages/saves'));
const LeaderboardPage = lazy(() => import('./pages/leaderboard'));
const PollsPage = lazy(() => import('./pages/polls'));
const SharedPollPage = lazy(() => import('./pages/shared-poll'));
const SquareSandboxLab = lazy(() => import('./pages/square-sandbox-lab'));

const RouteLoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center px-6">
    <div className="text-center space-y-3">
      <div className="mx-auto h-10 w-10 rounded-full border-4 border-muted border-t-primary animate-spin" />
      <p className="text-sm text-muted-foreground">Loading page…</p>
    </div>
  </div>
);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const Routes = () => {
  const { user } = useAuth();
  const isSquareLabAuthorized = String(user?.email ?? '').trim().toLowerCase() === 'dev@team.edu';

  // Only a real share opens a menu signed out: a poll view (?sharedView=poll,
  // or a poll option link's sharedPollToken) or a template token. Any other
  // sharedView value (?sharedView=1) used to skip sign-in too, but the page then
  // ran as the coach view and showed the coach header over "permission denied
  // for table restaurants".
  const allowSharedRestaurantView = (location) => {
    const params = new URLSearchParams(location.search || '');
    const sharedView = (params.get('sharedView') || '').toLowerCase();
    const hasTemplateToken = Boolean(params.get('sharedTemplateToken'));
    const hasPollToken = Boolean(params.get('sharedPollToken'));
    const hasSharedState = Boolean(location.state?.sharedTemplate || location.state?.sharedPollViewer);
    return sharedView === 'poll' || hasTemplateToken || hasPollToken || hasSharedState;
  };

  // "View team leaderboard" in the shared-cart drawer sent a signed-out guest
  // straight to /login-registration, because the route was unconditionally
  // protected. The cart id rides in the query string rather than router state so
  // the page survives a refresh and a copied link; it is the same capability
  // /shared-cart/:cartId already hands that visitor, and the leaderboard data
  // itself comes from get_team_leaderboard, which is already granted to anon.
  const allowSharedLeaderboardView = (location) => {
    const params = new URLSearchParams(location.search || '');
    return UUID_RE.test(String(params.get('sharedCartId') || '').trim());
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AssistantProvider>
      <DoordashConnectProvider>
      <ScrollToTop />
      <Suspense fallback={<RouteLoadingFallback />}>
      <RouterRoutes>
        <Route path="/" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
        <Route path="/order-history-management" element={<ProtectedRoute><OrderHistoryManagement /></ProtectedRoute>} />
        <Route path="/login-registration" element={<LoginRegistration />} />
        <Route path="/dashboard-home" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
        <Route path="/calendar-order-scheduling" element={<ProtectedRoute><CalendarOrderScheduling /></ProtectedRoute>} />
        <Route path="/team-setup" element={<ProtectedRoute><TeamSetup /></ProtectedRoute>} />
        <Route path="/team-members-management" element={<ProtectedRoute><TeamMembersManagement /></ProtectedRoute>} />
        <Route path="/payment-methods-billing" element={<ProtectedRoute><PaymentMethodsBilling /></ProtectedRoute>} />
        <Route path="/saves" element={<ProtectedRoute><SavesPage /></ProtectedRoute>} />
        <Route path="/terms-and-conditions" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/sms-opt-in-proof" element={<SmsOptInProof />} />
        <Route path="/awaiting-email-verification" element={<AwaitingEmailVerificationPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/home-restaurant-discovery" element={<ProtectedRoute><HomeRestaurantDiscovery /></ProtectedRoute>} />
        <Route
          path="/restaurant/:restaurantId"
          element={
            <ProtectedRoute allowAnonymous={allowSharedRestaurantView} signedOutFallback={<SignInPrompt />}>
              <RestaurantDetailMenu />
            </ProtectedRoute>
          }
        />
        <Route path="/shopping-cart-checkout" element={<ProtectedRoute><ShoppingCartCheckout /></ProtectedRoute>} />
        <Route path="/shared-cart/:cartId" element={<SharedCartMenu />} />
        <Route path="/shared-template/:token" element={<SharedTemplatePage />} />
        <Route path="/shared-poll/:token" element={<SharedPollPage />} />
        <Route path="/pay" element={<ProtectedRoute><PayPage /></ProtectedRoute>} />
        <Route path="/order/success" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute allowAnonymous={allowSharedLeaderboardView}>
              <LeaderboardPage />
            </ProtectedRoute>
          }
        />
        <Route path="/polls" element={<ProtectedRoute><PollsPage /></ProtectedRoute>} />
        <Route path="/assistant" element={<ProtectedRoute><OpenAssistantRedirect /></ProtectedRoute>} />
        <Route
          path="/square-sandbox-lab"
          element={
            <ProtectedRoute>
              {isSquareLabAuthorized ? <SquareSandboxLab /> : <Navigate to="/dashboard-home" replace />}
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </Suspense>
      {user && <AssistantPopup />}
      </DoordashConnectProvider>
      </AssistantProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
